﻿function toArray(value) {
  if (!value) {
    return [];
  }
  if (Array.isArray(value)) {
    return value;
  }
  return [value];
}

function coerceString(value, fallback = '') {
  return typeof value === 'string' ? value : fallback;
}

function normalizeAttachment(attachment) {
  if (!attachment || typeof attachment !== 'object') {
    return null;
  }
  return {
    name: coerceString(attachment.name, ''),
    size: Number(attachment.size || 0),
    type: coerceString(attachment.type, ''),
  };
}

export class PromptLibraryService {
  constructor({
    settings = {},
    saveSettings = () => undefined,
    now = () => new Date(),
    generateId = () => `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`,
  } = {}) {
    this.settings = settings;
    this.saveSettings = typeof saveSettings === 'function' ? saveSettings : () => undefined;
    this.now = typeof now === 'function' ? now : () => new Date();
    this.generateId = typeof generateId === 'function' ? generateId : () => `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`;

    if (!Array.isArray(this.settings.library)) {
      this.settings.library = [];
    }
  }

  listPrompts() {
    return this.settings.library.map((item) => ({ ...item }));
  }

  getPrompt(id) {
    if (!id) {
      return null;
    }
    return this.settings.library.find((item) => item && item.id === id) || null;
  }

  createPrompt({
    title = 'Prompt',
    persona = 'Agile Coach',
    lens = 'auto',
    team = '',
    event = 'daily',
    timebox = '15 min',
    notes = '',
    content = '',
    attachments = [],
    teamId = null,
    tool = null,
  } = {}) {
    const normalizedAttachments = toArray(attachments)
      .map((item) => normalizeAttachment(item))
      .filter((item) => item && item.name);

    const prompt = {
      id: this.generateId(),
      title: String(title || '').slice(0, 120) || 'Prompt',
      persona: persona || 'Agile Coach',
      lens: lens || 'auto',
      team: team || '',
      event: event || 'daily',
      timebox: timebox || '15 min',
      notes: notes || '',
      content: content || '',
      attachments: normalizedAttachments,
      teamId: teamId || null,
      tool: tool || null,
      createdAt: this.now().toISOString(),
    };

    this.settings.library.unshift(prompt);
    this.saveSettings(this.settings);
    return prompt;
  }

  deletePrompt(id) {
    if (!id) {
      return false;
    }
    const initialLength = this.settings.library.length;
    this.settings.library = this.settings.library.filter((item) => item?.id !== id);
    const removed = this.settings.library.length !== initialLength;
    if (removed) {
      this.saveSettings(this.settings);
    }
    return removed;
  }

  exportPrompts() {
    return JSON.stringify(this.settings.library, null, 2);
  }

  importPrompts(raw) {
    let parsed;
    try {
      parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    } catch (error) {
      throw new Error('Invalid JSON');
    }
    if (!Array.isArray(parsed)) {
      throw new Error('Invalid JSON');
    }

    const existing = new Set(this.settings.library.map((item) => item?.id).filter(Boolean));
    let added = 0;

    parsed.forEach((entry) => {
      if (!entry || typeof entry !== 'object') {
        return;
      }
      const id = entry.id || this.generateId();
      if (existing.has(id)) {
        return;
      }
      existing.add(id);
      const normalized = {
        id,
        title: String(entry.title || 'Prompt'),
        persona: entry.persona || 'Agile Coach',
        lens: entry.lens || 'auto',
        team: entry.team || '',
        event: entry.event || 'daily',
        timebox: entry.timebox || '15 min',
        notes: entry.notes || '',
        content: entry.content || entry.prompt || '',
        attachments: toArray(entry.attachments)
          .map((item) => normalizeAttachment(item))
          .filter(Boolean),
        teamId: entry.teamId || null,
        tool: entry.tool || null,
        createdAt: entry.createdAt || this.now().toISOString(),
      };
      this.settings.library.push(normalized);
      added += 1;
    });

    if (added > 0) {
      this.saveSettings(this.settings);
    }

    return { added, total: parsed.length };
  }

  getSettings() {
    return this.settings;
  }

  sharePrompt(id) {
    const prompt = this.getPrompt(id);
    if (!prompt) {
      throw new Error('Prompt not found');
    }
    return JSON.stringify(prompt, null, 2);
  }
}

if (typeof window !== 'undefined') {
  window.AgileProServices = window.AgileProServices || {};
  window.AgileProServices.PromptLibraryService = PromptLibraryService;
}



